// noinspection JSUnusedLocalSymbols

import { UserDomainModel } from '../../../domainModels/user-domain-model';
import { AuthFlow } from '../../../models/account/enum/auth-flow.enum';
import { SignInRequest } from '../../../models/account/requests/sign-in-request';
import { BehaviorSubject, iif, NEVER, Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { ForgotPasswordRequest } from '../../../models/account/requests/forgot-password-request';
import { CodeDeliveryDetails } from '../../../models/account/dto/code-delivery-details';
import { ResetPasswordRequest } from '../../../models/account/requests/reset-password-request';
import { SignInNewPasswordRequest } from '../../../models/account/requests/sign-in-new-password-request';
import { CacheService } from '../../../services/cache-service';
import { BaseViewModel } from '../../../models/base/base-view-model';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { HydratedAdminUser } from '../../../models/account/dto/hydrated-admin-user';
import { exists } from '../../../functions/exists';

/**
 * Must be provided by root, because it cannot be provided by the AuthComponent. The auth component is created/destroyed
 * on every url navigation. This means setupBindings() gets called every time the component is created, which is not
 * what we want. We want to keep the subscriptions alive inside setupBindings() until the auth flow is complete.
 *
 * This means that when the AuthViewModel is created, it stays alive for the life of the program. Therefore,
 * setupBindings() checks through navigation events if we are in the auth flow, and if we are, it emits values to
 * subscribers. If we are not in the auth flow, then subscribers are not notified.
 */
@Injectable({ providedIn: 'root' })
export class AuthViewModel extends BaseViewModel {
  constructor(
    private userDomainModel: UserDomainModel,
    private cacheService: CacheService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super();
    this.setupBindings();
  }

  private readonly _authFlow = new BehaviorSubject<AuthFlow>(AuthFlow.SignIn);
  public readonly authFlow$ = this._authFlow.pipe(distinctUntilChanged());
  connectToAuthFlow = (authFlow: AuthFlow) => this._authFlow.next(authFlow);

  public nextAuthFlow: BehaviorSubject<AuthFlow> = new BehaviorSubject<AuthFlow>(null);
  public authSuccessNavigateToHome: Subject<void> = new Subject<void>();

  public user$ = this.userDomainModel.user$;

  private _preFillEmail = new BehaviorSubject<string>('');
  public readonly preFillEmail$ = this._preFillEmail as Observable<string>;

  private _preFillCode = new BehaviorSubject<string>('');
  public readonly preFillCode$ = this._preFillCode as Observable<string>;

  private _preFillPassword = new BehaviorSubject<string>('');
  public readonly preFillPassword$ = this._preFillPassword as Observable<string>;

  private listenToQueryParams = this.route.queryParams.subscribeWhileAlive({
    owner: this,
    next: params => {
      const email = decodeURIComponent(decodeURIComponent(params?.Email || params?.email || ''));
      const code = decodeURIComponent(decodeURIComponent(params?.Code || params?.code || ''));
      const password = decodeURIComponent(decodeURIComponent(params?.Password || params?.password || ''));
      if (exists(email)) this._preFillEmail.next(email);
      if (exists(code)) this._preFillCode.next(code);
      if (exists(password)) this._preFillPassword.next(password);
      if (exists(email) || exists(code) || exists(password)) {
        this.router.navigate([], { queryParams: {}, replaceUrl: true, relativeTo: this.route }).then();
      }
    }
  });

  // Misc
  // @ts-ignore
  public versionString = require('package.json')?.version;

  public readonly isSignIn$ = this.authFlow$.pipe(map(authFlow => authFlow === AuthFlow.SignIn));

  public readonly isForgotPassword$ = this.authFlow$.pipe(map(authFlow => authFlow === AuthFlow.ForgotPassword));

  public readonly isResetPassword$ = this.authFlow$.pipe(map(authFlow => authFlow === AuthFlow.ResetPassword));

  public readonly isSetNewPassword$ = this.authFlow$.pipe(map(authFlow => authFlow === AuthFlow.SetNewPassword));

  setupBindings() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map((navigationEndEvent: NavigationEnd) => navigationEndEvent.url?.includes('auth/')),
        distinctUntilChanged(),
        switchMap(isAuthFlow => iif(() => isAuthFlow, this.userDomainModel.user$, NEVER))
      )
      .subscribeWhileAlive({
        owner: this,
        next: user => {
          if (user?.session?.hasChallenge()) {
            this.nextAuthFlow.next(AuthFlow.SetNewPassword);
          } else if (user?.session?.validSession()) {
            this.authSuccessNavigateToHome.next();
          } else if (user?.codeDeliveryDetails?.length > 0) {
            this.nextAuthFlow.next(AuthFlow.ResetPassword);
          } else if (exists(user)) {
            this.nextAuthFlow.next(AuthFlow.SignIn);
          }
        }
      });
  }

  clearUserAndGetBackToSignIn(): void {
    this.userDomainModel.setUser(null);
    this.nextAuthFlow.next(AuthFlow.SignIn);
  }

  public readonly signingInMsg = 'Signing In';

  public signIn$(req: SignInRequest): Observable<HydratedAdminUser> {
    return this.tapIntoUser$(this.userDomainModel.signIn$(req));
  }

  public forgotPassword$(req: ForgotPasswordRequest): Observable<CodeDeliveryDetails> {
    return this.userDomainModel.forgotPassword$(req);
  }

  public resetPassword$(req: ResetPasswordRequest): Observable<HydratedAdminUser> {
    return this.tapIntoUser$(this.userDomainModel.resetPassword$(req));
  }

  public signInNewPassword$(req: SignInNewPasswordRequest): Observable<HydratedAdminUser> {
    return this.user$.pipe(
      take(1),
      switchMap(user => {
        req.userId = user?.userId;
        req.session = user?.session?.getChallengeAuthToken();
        return this.tapIntoUser$(this.userDomainModel.signInNewPassword$(req));
      })
    );
  }

  private tapIntoUser$(user$: Observable<HydratedAdminUser | null>): Observable<HydratedAdminUser | null> {
    return user$.pipe(
      tap({
        complete: () => {
          this._preFillEmail.next('');
          this._preFillCode.next('');
          this._preFillPassword.next('');
        }
      })
    );
  }

  signedIn(): void {
    this._loadingOpts.removeRequest(this.signingInMsg);
  }

  signInFailed(): void {
    this._loadingOpts.removeRequest(this.signingInMsg);
  }

  clearCaches() {
    this.cacheService.clearAllCaches();
  }
}
